import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';

interface TrailPoint {
  x: number;
  y: number;
  age: number; // 0 = newest, 1 = oldest
}

const MAX_TRAIL = 20;
const FADE_SPEED = 0.06;

const CursorTrail: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { darkMode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const trail: TrailPoint[] = [];
    let mouseX = -200;
    let mouseY = -200;
    let animId: number;

    // Skip on touch-only devices
    const isTouchOnly = window.matchMedia('(hover: none)').matches;
    if (isTouchOnly) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      trail.unshift({ x: mouseX, y: mouseY, age: 0 });
      if (trail.length > MAX_TRAIL) trail.pop();
    };

    const handleResize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    const baseColor = darkMode ? '0, 255, 136' : '0, 102, 204';

    const draw = () => {
      animId = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Advance ages
      for (let i = trail.length - 1; i >= 0; i--) {
        trail[i].age = Math.min(1, trail[i].age + FADE_SPEED);
      }
      // Remove fully faded points
      while (trail.length > 0 && trail[trail.length - 1].age >= 1) {
        trail.pop();
      }

      // Draw each point
      trail.forEach((pt, i) => {
        const progress = i / MAX_TRAIL;          // 0 = head, 1 = tail
        const alpha    = (1 - progress) * (1 - pt.age) * 0.65;
        const radius   = (1 - progress) * 7 + 1.5;

        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${baseColor}, ${alpha})`;
        ctx.fill();
      });

      // Draw crisp cursor dot
      ctx.beginPath();
      ctx.arc(mouseX, mouseY, 4, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${baseColor}, 0.85)`;
      ctx.fill();
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [darkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
};

export default CursorTrail;
